import React, { useCallback, useEffect, useRef, useState } from 'react';
import { classService, getErrorMessage, lessonService, lessonThumbnailSrc, submissionService } from '../services/api';
import type { Class, ClassStudent, LessonAssignment, LessonSummary, StudentProgress } from '../types/api';
import {
  Plus,
  Users,
  BookOpen,
  Download,
  Trash2,
  ClipboardList,
  ChevronDown,
  Settings,
  AlertTriangle,
  Save,
  Copy,
  Check,
  UserMinus,
  RefreshCw,
  Mail,
  X,
  Link as LinkIcon,
} from 'lucide-react';
import AppNavBar from '../components/AppNavBar';
import ClassLessonManager from '../components/ClassLessonManager';
import SubmissionsPanel from '../components/SubmissionsPanel';
import ContentLibrary from '../components/ContentLibrary';
import StatusBadge from '../components/StatusBadge';
import ReadinessBadge from '../components/ReadinessBadge';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { getInviteLink } from '../utils/invite';
import ClassPlanBanner from '../components/ClassPlanBanner';
import { EmptyState, LoadingState, Modal, Spinner } from '../components/ui';

const ALL_PROGRESS_FILTER = 'all';

// Quote every field, and defuse values a spreadsheet would run as a formula (student names are user input).
function toCsvField(value: string): string {
  const neutralised = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${neutralised.replace(/"/g, '""')}"`;
}

function inviteEmailText(className: string, inviteCode: string): string {
  return [
    `Hi everyone,`,
    ``,
    `we'll use Webots Academy in ${className} to program robots in a simulator, right in the browser.`,
    ``,
    `Join the class here: ${getInviteLink(inviteCode)}`,
    `(or sign in at ${window.location.origin} and enter the class code ${inviteCode}).`,
    ``,
    `See you there!`,
  ].join('\n');
}

const TeacherDashboard: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [assignedLessons, setAssignedLessons] = useState<LessonSummary[]>([]);
  const [classAssignments, setClassAssignments] = useState<LessonAssignment[] | null>(null);
  const [progress, setProgress] = useState<StudentProgress[]>([]);
  const [selectedProgressStudentId, setSelectedProgressStudentId] = useState<string>(ALL_PROGRESS_FILTER);
  const [selectedProgressLessonId, setSelectedProgressLessonId] = useState<string>(ALL_PROGRESS_FILTER);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newClassDesc, setNewClassDesc] = useState('');
  const [selectedLessonIds, setSelectedLessonIds] = useState<number[]>([]);
  const [creatingClass, setCreatingClass] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'classes' | 'lessons'>('classes');
  const [classTab, setClassTab] = useState<'settings' | 'lessons' | 'students'>('lessons');
  const [lessons, setLessons] = useState<LessonSummary[]>([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [viewingSubmissions, setViewingSubmissions] = useState(false);

  // Settings tab state
  const [settingsClassName, setSettingsClassName] = useState('');
  const [settingsClassDesc, setSettingsClassDesc] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [regeneratingInvite, setRegeneratingInvite] = useState(false);
  const [removingStudentId, setRemovingStudentId] = useState<number | null>(null);
  const [copiedValue, setCopiedValue] = useState<'code' | 'link' | 'email' | null>(null);
  // The class just created: shows the "Invite your students" step until dismissed.
  const [newlyCreatedClassId, setNewlyCreatedClassId] = useState<number | null>(null);
  // Answers for a class the teacher has already switched away from are dropped.
  const selectRequestRef = useRef(0);

  const selectClass = useCallback(async (classData: Class) => {
    const requestId = ++selectRequestRef.current;
    setSelectedClass(classData);
    setProgress([]);
    setAssignedLessons([]);
    setClassAssignments(null);
    setSelectedProgressStudentId(ALL_PROGRESS_FILTER);
    setSelectedProgressLessonId(ALL_PROGRESS_FILTER);
    // Initialize settings fields when class is selected
    setSettingsClassName(classData.name);
    setSettingsClassDesc(classData.description || '');
    setDeleteConfirmation('');
    setClassTab('lessons'); // Reset to lessons tab when switching classes
    try {
      const [progressData, classLessons] = await Promise.all([
        submissionService.classProgress(classData.id),
        lessonService.listForClass(classData.id),
      ]);
      if (requestId !== selectRequestRef.current) {
        return;
      }
      setProgress(progressData);
      setAssignedLessons(classLessons);
    } catch (error) {
      console.error('Error loading class data:', error);
      if (requestId === selectRequestRef.current) {
        toast.error(`The class data could not be loaded: ${getErrorMessage(error)}`);
      }
    }
  }, [toast]);

  const loadClasses = useCallback(async (classIdToSelect?: number) => {
    try {
      const data = await classService.getClasses();
      setClasses(data);
      if (data.length > 0) {
        const classToSelect = (classIdToSelect && data.find((cls) => cls.id === classIdToSelect)) || data[0];
        selectClass(classToSelect);
      } else {
        setSelectedClass(null);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
      toast.error(`Your classes could not be loaded: ${getErrorMessage(error)}`);
    } finally {
      setLoading(false);
    }
  }, [selectClass, toast]);

  const loadLessons = useCallback(async () => {
    setLessonsLoading(true);
    try {
      setLessons(await lessonService.list());
    } catch (error) {
      console.error('Error loading lessons:', error);
    } finally {
      setLessonsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClasses();
    loadLessons();
  }, [loadClasses, loadLessons]);

  useEffect(() => {
    if (
      selectedProgressStudentId !== ALL_PROGRESS_FILTER
      && !progress.some((row) => row.student_id === Number(selectedProgressStudentId))
    ) {
      setSelectedProgressStudentId(ALL_PROGRESS_FILTER);
    }

    if (
      selectedProgressLessonId !== ALL_PROGRESS_FILTER
      && !progress.some((row) => row.lesson_id === Number(selectedProgressLessonId))
    ) {
      setSelectedProgressLessonId(ALL_PROGRESS_FILTER);
    }
  }, [progress, selectedProgressStudentId, selectedProgressLessonId]);

  // The lesson manager reports every change; keep the lessons offered for review in step with it.
  const handleAssignmentsChange = useCallback((assignments: LessonAssignment[]) => {
    setClassAssignments(assignments);
  }, []);

  const lessonsForReview = classAssignments
    ? classAssignments
      .map((assignment) => lessons.find((lesson) => lesson.id === assignment.lesson_id) || assignedLessons.find((lesson) => lesson.id === assignment.lesson_id))
      .filter((lesson): lesson is LessonSummary => Boolean(lesson))
    : assignedLessons;

  const openCreateClass = () => {
    setNewClassName('');
    setNewClassDesc('');
    setSelectedLessonIds([]);
    setShowCreateClass(true);
  };

  const createClass = async () => {
    if (!newClassName.trim()) {
      toast.error('Give the class a name.');
      return;
    }
    setCreatingClass(true);
    try {
      // The selection is sent as is, an empty list included: the backend assigns every lesson only when
      // lesson_ids is left out.
      const createdClass = await classService.createClass({
        name: newClassName,
        description: newClassDesc,
        lesson_ids: selectedLessonIds,
      });
      setNewlyCreatedClassId(createdClass.id);
      setShowCreateClass(false);
      setNewClassName('');
      setNewClassDesc('');
      setSelectedLessonIds([]);
      setActiveTab('classes');
      loadClasses(createdClass.id);
    } catch (error) {
      console.error('Error creating class:', error);
      toast.error(`Failed to create the class: ${getErrorMessage(error)}`);
    } finally {
      setCreatingClass(false);
    }
  };

  const copyInviteValue = async (value: string, valueType: 'code' | 'link' | 'email') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedValue(valueType);
      setTimeout(() => setCopiedValue(null), 2000);
    } catch (error) {
      console.error('Failed to copy invite value:', error);
      toast.error('Unable to copy. Please copy manually.');
    }
  };

  const replaceClass = (updatedClass: Class) => {
    setClasses(prev => prev.map(c => (c.id === updatedClass.id ? { ...c, ...updatedClass } : c)));
    setSelectedClass(prev => (prev && prev.id === updatedClass.id ? { ...prev, ...updatedClass } : prev));
  };

  const saveClassSettings = async () => {
    if (!selectedClass) return;

    setIsSaving(true);
    try {
      const updatedClass = await classService.updateClass(selectedClass.id, {
        name: settingsClassName,
        description: settingsClassDesc
      });
      replaceClass(updatedClass);
      toast.success('Class settings saved');
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error(`Failed to save class settings: ${getErrorMessage(error)}`);
    } finally {
      setIsSaving(false);
    }
  };

  const regenerateInviteLink = async () => {
    if (!selectedClass) return;
    const confirmed = await confirm({
      title: 'Replace the invite link?',
      message: 'The current link and class code stop working right away. Students already in the class stay enrolled; share the new link with anyone who still has to join.',
      confirmLabel: 'Replace link',
      danger: true,
    });
    if (!confirmed) {
      return;
    }
    setRegeneratingInvite(true);
    try {
      replaceClass(await classService.regenerateInviteCode(selectedClass.id));
      toast.success('New invite link created. The old one no longer works.');
    } catch (error) {
      console.error('Error regenerating the invite code:', error);
      toast.error(`Failed to replace the invite link: ${getErrorMessage(error)}`);
    } finally {
      setRegeneratingInvite(false);
    }
  };

  const deleteClassWithConfirmation = async (classId: number) => {
    // Already validated in the UI that deleteConfirmation matches className
    try {
      await classService.deleteClass(classId);
      if (selectedClass?.id === classId) {
        setSelectedClass(null);
      }
      setDeleteConfirmation('');
      await loadClasses();
      toast.success('Class deleted');
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error(`Failed to delete class: ${getErrorMessage(error)}`);
    }
  };

  const unregisterStudent = async (student: ClassStudent) => {
    if (!selectedClass) return;

    const shouldUnregister = await confirm({
      title: `Remove ${student.full_name}?`,
      message: `They leave "${selectedClass.name}" and can rejoin using the invite link or code.`,
      confirmLabel: 'Remove student',
      danger: true,
    });
    if (!shouldUnregister) {
      return;
    }

    setRemovingStudentId(student.id);
    try {
      replaceClass(await classService.removeStudent(selectedClass.id, student.id));
      setProgress(prev => prev.filter(item => item.student_id !== student.id));
    } catch (error) {
      console.error('Error unregistering student:', error);
      toast.error(getErrorMessage(error, 'Failed to unregister student'));
    } finally {
      setRemovingStudentId(null);
    }
  };

  const toggleLessonSelection = (lessonId: number) => {
    setSelectedLessonIds(prev =>
      prev.includes(lessonId)
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId]
    );
  };

  const officialLessons = lessons.filter(l => l.lesson_type === 'official');
  const customLessons = lessons.filter(l => l.lesson_type === 'custom');
  const progressStudents = Array.from(
    progress.reduce((map, row) => {
      if (!map.has(row.student_id)) {
        map.set(row.student_id, { id: row.student_id, name: row.student_name });
      }
      return map;
    }, new Map<number, { id: number; name: string }>())
      .values()
  ).sort((a, b) => a.name.localeCompare(b.name));
  const progressLessons = Array.from(
    progress.reduce((map, row) => {
      if (!map.has(row.lesson_id)) {
        map.set(row.lesson_id, { id: row.lesson_id, title: row.lesson_title });
      }
      return map;
    }, new Map<number, { id: number; title: string }>())
      .values()
  ).sort((a, b) => a.title.localeCompare(b.title));
  const filteredProgress = progress.filter((row) => {
    const matchesStudent = selectedProgressStudentId === ALL_PROGRESS_FILTER
      || row.student_id === Number(selectedProgressStudentId);
    const matchesLesson = selectedProgressLessonId === ALL_PROGRESS_FILTER
      || row.lesson_id === Number(selectedProgressLessonId);
    return matchesStudent && matchesLesson;
  });

  const exportCSV = () => {
    if (!filteredProgress.length) return;

    const headers = ['Student Name', 'Email', 'Lesson', 'Status'];
    const rows = filteredProgress.map(p => [
      p.student_name,
      p.student_email,
      p.lesson_title,
      p.status,
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(toCsvField).join(','))
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedClass?.name || 'class'}_progress.csv`;
    a.click();
    // Let the download start before the URL goes away.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const renderLessonOption = (lesson: LessonSummary) => {
    const thumbnail = lessonThumbnailSrc(lesson);
    const checked = selectedLessonIds.includes(lesson.id);
    return (
      <label
        key={lesson.id}
        className={`flex items-start gap-3 cursor-pointer rounded-lg border p-2.5 transition-colors ${checked
          ? 'border-blue-300 bg-blue-50/70 dark:border-blue-800 dark:bg-blue-950/40'
          : 'border-transparent hover:bg-white dark:hover:bg-gray-800'
          }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => toggleLessonSelection(lesson.id)}
          className="checkbox mt-1"
        />
        <div className="flex gap-3 flex-1 min-w-0">
          {thumbnail && (
            <div className="w-16 h-12 flex-shrink-0 hidden sm:block">
              <img
                src={thumbnail}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover rounded-md border border-gray-200 dark:border-gray-700"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-gray-900 dark:text-white">{lesson.title}<ReadinessBadge readiness={lesson.readiness} plain /></div>
            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{lesson.description}</div>
          </div>
        </div>
      </label>
    );
  };

  const subTabClasses = (active: boolean) => `relative flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors [&_svg]:w-4 [&_svg]:h-4 [&_svg]:shrink-0 ${active
    ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
    : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300 dark:text-gray-400 dark:hover:text-white dark:hover:border-gray-600'
    }`;

  const copyButtonClasses = 'btn btn-secondary shrink-0';

  const closeCreateClass = () => {
    setShowCreateClass(false);
    setSelectedLessonIds([]);
  };

  return (
    <div className="page-shell">
      <AppNavBar title="Teacher Console" roleLabel="Professor" />

      <div className="page-container">
        {/* Navigation Tabs */}
        <div className="tabs mb-6 sm:mb-8">
          <button onClick={() => setActiveTab('classes')} className={`tab ${activeTab === 'classes' ? 'tab-active' : ''}`} aria-pressed={activeTab === 'classes'}>
            <Users />
            My Classes
          </button>
          <button onClick={() => setActiveTab('lessons')} className={`tab ${activeTab === 'lessons' ? 'tab-active' : ''}`} aria-pressed={activeTab === 'lessons'}>
            <BookOpen />
            Content Library
          </button>
        </div>

        {activeTab === 'classes' ? (
          <>
            {/* Header with class selector and invite access */}
            <div className="page-header">
              <div className="min-w-0">
                <h2 className="page-title">My Classes</h2>
                <p className="page-subtitle">Arrange lessons, invite students and review their work.</p>
              </div>
              {/* The main action only until there is a class; then the class's own work is. */}
              <button onClick={openCreateClass} className={`btn btn-lg shrink-0 ${!loading && classes.length === 0 ? 'btn-primary' : 'btn-secondary'}`}>
                <Plus />
                <span>Create New Class</span>
              </button>
            </div>

            {loading ? (
              <LoadingState label="Loading your classes..." />
            ) : classes.length > 0 && selectedClass && (
              <div className="card mb-6 p-3 sm:p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="relative w-full lg:w-72 shrink-0">
                  <label htmlFor="teacher-class-select" className="sr-only">Class</label>
                  <select
                    id="teacher-class-select"
                    value={selectedClass?.id || ''}
                    onChange={(e) => {
                      const cls = classes.find(c => c.id === Number(e.target.value));
                      if (cls) selectClass(cls);
                    }}
                    className="select font-semibold"
                  >
                    {classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                <div className="min-w-0 flex-1 flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 pl-3 pr-1 py-1">
                  <span className="eyebrow shrink-0 hidden sm:inline">Invite link</span>
                  <LinkIcon className="w-4 h-4 text-gray-400 shrink-0 sm:hidden" />
                  <code
                    className="text-sm text-gray-700 dark:text-gray-200 truncate min-w-0 flex-1"
                    title={getInviteLink(selectedClass.invite_code)}
                  >
                    {getInviteLink(selectedClass.invite_code)}
                  </code>
                  <button
                    onClick={() => copyInviteValue(getInviteLink(selectedClass.invite_code), 'link')}
                    className="btn btn-secondary btn-sm shrink-0"
                  >
                    {copiedValue === 'link' ? <Check /> : <Copy />}
                    {copiedValue === 'link' ? 'Copied' : 'Copy Link'}
                  </button>
                </div>
              </div>
            )}
            {!loading && selectedClass && <ClassPlanBanner cls={selectedClass} />}

            {showCreateClass && (
              <Modal
                title="Create New Class"
                description="Name the class and pick the lessons to start with."
                onClose={closeCreateClass}
                size="lg"
                footer={
                  <>
                    <button onClick={closeCreateClass} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button onClick={createClass} disabled={creatingClass} className="btn btn-primary">
                      {creatingClass && <Spinner className="w-4 h-4 text-white" />}
                      Create
                    </button>
                  </>
                }
              >
                <div className="space-y-4">
                  <div>
                    <label htmlFor="new-class-name" className="label">Class Name</label>
                    <input
                      id="new-class-name"
                      type="text"
                      value={newClassName}
                      onChange={(e) => setNewClassName(e.target.value)}
                      className="input"
                      placeholder="e.g. Robotics 101, Fall 2026"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label htmlFor="new-class-description" className="label">Description</label>
                    <textarea
                      id="new-class-description"
                      value={newClassDesc}
                      onChange={(e) => setNewClassDesc(e.target.value)}
                      className="input"
                      rows={3}
                    />
                  </div>

                  <div>
                    <div className="flex flex-wrap justify-between items-center gap-2 mb-1">
                      <span className="label mb-0">Lessons</span>
                      <div className="flex items-center gap-1 text-sm">
                        <button onClick={() => setSelectedLessonIds(lessons.map(l => l.id))} className="link px-1">
                          Select all
                        </button>
                        <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
                        <button onClick={() => setSelectedLessonIds([])} className="link px-1">
                          Clear
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      Pick the lessons to start with. You can add, remove and reorder lessons at any time.
                    </p>

                    {lessonsLoading ? (
                      <LoadingState label="Loading lessons..." className="py-6" />
                    ) : (
                      <div className="card-muted p-2 max-h-64 overflow-y-auto space-y-1">
                        {customLessons.length > 0 && (
                          <div className="mb-3">
                            <h4 className="eyebrow px-2.5 pt-1.5 pb-1">Your Custom Lessons</h4>
                            {customLessons.map(renderLessonOption)}
                          </div>
                        )}

                        {officialLessons.length > 0 && (
                          <div>
                            <h4 className="eyebrow px-2.5 pt-1.5 pb-1">Official Lessons</h4>
                            {officialLessons.map(renderLessonOption)}
                          </div>
                        )}

                        {lessons.length === 0 && (
                          <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                            No lessons available. Create lessons first.
                          </div>
                        )}
                      </div>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      {selectedLessonIds.length === 0
                        ? 'No lessons selected: the class starts empty.'
                        : `${selectedLessonIds.length} lesson${selectedLessonIds.length !== 1 ? 's' : ''} selected`}
                    </p>
                  </div>
                </div>
              </Modal>
            )}

            {loading ? null : classes.length === 0 ? (
              <div className="card p-6 sm:p-8">
                <h3 className="section-title text-xl mb-1">Set up your first class</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Three steps, a few minutes:</p>
                <ol className="grid gap-4 sm:grid-cols-3 mb-8">
                  {[
                    ['Create a class', 'Give it a name and pick the lessons to start with.'],
                    ['Arrange the lessons', 'Order them, hide what is not ready yet, and set submission deadlines.'],
                    ['Invite your students', 'Share the invite link: students register and land in your class.'],
                  ].map(([title, text], index) => (
                    <li key={title} className="card-muted p-4 flex gap-3 sm:flex-col">
                      <span className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center shrink-0">{index + 1}</span>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{title}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <button onClick={openCreateClass} className="btn btn-primary btn-lg">
                  <Plus />
                  Create New Class
                </button>
              </div>
            ) : selectedClass ? (
              <>
                {newlyCreatedClassId === selectedClass.id && (
                  // Shown right after creating a class; not a modal, so the dashboard stays usable.
                  <div className="mb-6 rounded-xl border border-emerald-200 dark:border-emerald-900/70 bg-emerald-50 dark:bg-emerald-950/30 p-4 sm:p-6">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white"><Check className="w-5 h-5" /></span>
                        <div>
                          <h3 className="text-lg font-semibold text-emerald-950 dark:text-emerald-50">Class created. Invite your students</h3>
                          <p className="text-sm text-emerald-800 dark:text-emerald-200">Send them the link below; it registers them and enrolls them in "{selectedClass.name}".</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setNewlyCreatedClassId(null)}
                        className="btn btn-ghost btn-icon text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
                        aria-label="Dismiss"
                      >
                        <X />
                      </button>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mb-3">
                      <code className="flex-1 min-w-0 break-all bg-white dark:bg-gray-900 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 border border-emerald-200 dark:border-emerald-900">
                        {getInviteLink(selectedClass.invite_code)}
                      </code>
                      <button onClick={() => copyInviteValue(getInviteLink(selectedClass.invite_code), 'link')} className={copyButtonClasses}>
                        {copiedValue === 'link' ? <Check /> : <Copy />}
                        {copiedValue === 'link' ? 'Copied' : 'Copy Link'}
                      </button>
                      <button onClick={() => copyInviteValue(inviteEmailText(selectedClass.name, selectedClass.invite_code), 'email')} className={copyButtonClasses}>
                        {copiedValue === 'email' ? <Check /> : <Mail />}
                        {copiedValue === 'email' ? 'Copied' : 'Copy Email Text'}
                      </button>
                    </div>
                    <details className="text-sm">
                      <summary className="cursor-pointer text-emerald-800 dark:text-emerald-200 font-medium">Preview the email text</summary>
                      <pre className="mt-2 whitespace-pre-wrap break-words bg-white dark:bg-gray-900 rounded-lg p-3 text-gray-700 dark:text-gray-300 border border-emerald-200 dark:border-emerald-900 font-sans">
                        {inviteEmailText(selectedClass.name, selectedClass.invite_code)}
                      </pre>
                    </details>
                  </div>
                )}

                <div className="card overflow-hidden">
                  {/* Sub-tabs for selected class */}
                  <div className="flex gap-1 px-2 sm:px-4 border-b border-gray-200 dark:border-gray-800 overflow-x-auto" role="tablist">
                    <button onClick={() => setClassTab('lessons')} className={subTabClasses(classTab === 'lessons')} role="tab" aria-selected={classTab === 'lessons'}>
                      <BookOpen />
                      Lessons
                    </button>
                    <button onClick={() => setClassTab('students')} className={subTabClasses(classTab === 'students')} role="tab" aria-selected={classTab === 'students'}>
                      <Users />
                      Students ({selectedClass.students?.length || 0})
                    </button>
                    <button onClick={() => setClassTab('settings')} className={subTabClasses(classTab === 'settings')} role="tab" aria-selected={classTab === 'settings'}>
                      <Settings />
                      Settings
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="p-4 sm:p-6">
                    {classTab === 'settings' && (
                      <div className="max-w-3xl">
                        <h3 className="section-title mb-1">Class Settings</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">The name and description students see, and how they join.</p>

                        {/* Editable Class Info */}
                        <div className="space-y-4 mb-6">
                          <div>
                            <label htmlFor="settings-class-name" className="label">
                              Class Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="settings-class-name"
                              type="text"
                              value={settingsClassName}
                              onChange={(e) => setSettingsClassName(e.target.value)}
                              className="input"
                              placeholder="Enter class name"
                            />
                          </div>

                          <div>
                            <label htmlFor="settings-class-description" className="label">Description</label>
                            <textarea
                              id="settings-class-description"
                              value={settingsClassDesc}
                              onChange={(e) => setSettingsClassDesc(e.target.value)}
                              className="input"
                              rows={3}
                              placeholder="Enter class description (optional)"
                            />
                          </div>

                          <div>
                            <span className="label">Invite Access</span>
                            <div className="card-muted divide-y divide-gray-200 dark:divide-gray-800">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-4">
                                <span className="eyebrow sm:w-24 shrink-0">Invite Code</span>
                                <code className="flex-1 min-w-0 text-xl font-mono font-semibold text-gray-900 dark:text-white tracking-widest break-all">
                                  {selectedClass.invite_code}
                                </code>
                                <button onClick={() => copyInviteValue(selectedClass.invite_code, 'code')} className={copyButtonClasses}>
                                  {copiedValue === 'code' ? <Check /> : <Copy />}
                                  {copiedValue === 'code' ? 'Copied' : 'Copy Code'}
                                </button>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-4">
                                <span className="eyebrow sm:w-24 shrink-0">Join Link</span>
                                <code className="flex-1 min-w-0 text-xs break-all text-gray-600 dark:text-gray-400 font-mono">
                                  {getInviteLink(selectedClass.invite_code)}
                                </code>
                                <button onClick={() => copyInviteValue(getInviteLink(selectedClass.invite_code), 'link')} className={copyButtonClasses}>
                                  {copiedValue === 'link' ? <Check /> : <Copy />}
                                  {copiedValue === 'link' ? 'Copied' : 'Copy Link'}
                                </button>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  Link shared too widely? Replace it: the old link and code stop working.
                                </p>
                                <button
                                  onClick={regenerateInviteLink}
                                  disabled={regeneratingInvite}
                                  className="btn btn-secondary shrink-0"
                                >
                                  <RefreshCw className={regeneratingInvite ? 'animate-spin' : ''} />
                                  Regenerate Invite Link
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Save Button */}
                        <div className="mb-10">
                          <button
                            onClick={saveClassSettings}
                            disabled={
                              isSaving ||
                              !settingsClassName.trim() ||
                              (settingsClassName === selectedClass.name && settingsClassDesc === (selectedClass.description || ''))
                            }
                            className="btn btn-primary w-full sm:w-auto"
                          >
                            <Save />
                            {isSaving ? 'Saving Changes...' : 'Save Class Settings'}
                          </button>
                        </div>

                        {/* Danger Zone */}
                        <div className="rounded-xl border border-red-200 dark:border-red-900/60 p-4 sm:p-6">
                          <div className="flex items-start gap-3 mb-5">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 dark:bg-red-950/50 shrink-0">
                              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            </div>
                            <div>
                              <h4 className="text-base font-semibold text-red-700 dark:text-red-300">Danger Zone</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Once you delete a class, there is no going back. This action cannot be undone and all student progress will be lost.
                              </p>
                            </div>
                          </div>

                          <div className="space-y-3 max-w-md">
                            <div>
                              <label htmlFor="delete-class-confirmation" className="label">
                                Confirm by typing <span className="font-semibold text-red-600 dark:text-red-400 break-all">{selectedClass.name}</span>
                              </label>
                              <input
                                id="delete-class-confirmation"
                                type="text"
                                value={deleteConfirmation}
                                onChange={(e) => setDeleteConfirmation(e.target.value)}
                                className="input focus:border-red-500 focus:ring-red-500/30"
                                placeholder="Enter class name to confirm"
                              />
                            </div>
                            <button
                              onClick={() => deleteClassWithConfirmation(selectedClass.id)}
                              disabled={deleteConfirmation !== selectedClass.name}
                              className="btn btn-danger w-full"
                            >
                              <Trash2 />
                              Delete Class Permanently
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {classTab === 'lessons' && (
                      <ClassLessonManager classId={selectedClass.id} onChange={handleAssignmentsChange} />
                    )}

                    {classTab === 'students' && (
                      <div>
                        <div className="mb-8">
                          <h3 className="section-title mb-4">Enrolled Students</h3>
                          {selectedClass.students?.length ? (
                            <ul className="divide-y divide-gray-100 dark:divide-gray-800 rounded-xl border border-gray-200 dark:border-gray-800">
                              {selectedClass.students.map((student) => (
                                <li
                                  key={student.id}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3"
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="avatar" aria-hidden="true">
                                      {student.full_name.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-medium text-gray-900 dark:text-white truncate">{student.full_name}</p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{student.email}</p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => unregisterStudent(student)}
                                    disabled={removingStudentId === student.id}
                                    className="btn btn-ghost-danger btn-sm self-start sm:self-auto"
                                  >
                                    <UserMinus />
                                    {removingStudentId === student.id ? 'Removing...' : 'Unregister Student'}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <EmptyState icon={<Users />} title="No students enrolled yet">
                              Share the invite link above to get them in.
                            </EmptyState>
                          )}
                        </div>

                        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                          <h3 className="section-title">Student Progress</h3>
                          <div className="flex flex-wrap gap-2">
                            {progress.length > 0 && (
                              <button
                                onClick={exportCSV}
                                disabled={filteredProgress.length === 0}
                                className="btn btn-secondary"
                              >
                                <Download />
                                Export CSV
                              </button>
                            )}
                            <button onClick={() => setViewingSubmissions(true)} className="btn btn-primary">
                              <ClipboardList />
                              View Detailed Submissions
                            </button>
                          </div>
                        </div>

                        {progress.length === 0 ? (
                          <EmptyState icon={<ClipboardList />} title="No progress data available yet.">
                            Progress shows up here once students open and submit lessons.
                          </EmptyState>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="progress-student-filter" className="label">Student Name</label>
                                <div className="relative">
                                  <select
                                    id="progress-student-filter"
                                    value={selectedProgressStudentId}
                                    onChange={(e) => setSelectedProgressStudentId(e.target.value)}
                                    className="select"
                                  >
                                    <option value={ALL_PROGRESS_FILTER}>All students</option>
                                    {progressStudents.map((student) => (
                                      <option key={student.id} value={student.id}>
                                        {student.name}
                                      </option>
                                    ))}
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                              </div>
                              <div>
                                <label htmlFor="progress-lesson-filter" className="label">Lesson</label>
                                <div className="relative">
                                  <select
                                    id="progress-lesson-filter"
                                    value={selectedProgressLessonId}
                                    onChange={(e) => setSelectedProgressLessonId(e.target.value)}
                                    className="select"
                                  >
                                    <option value={ALL_PROGRESS_FILTER}>All lessons</option>
                                    {progressLessons.map((lesson) => (
                                      <option key={lesson.id} value={lesson.id}>
                                        {lesson.title}
                                      </option>
                                    ))}
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                              </div>
                            </div>

                            {filteredProgress.length === 0 ? (
                              <EmptyState title="No progress matches the selected filters." />
                            ) : (
                              <>
                                {/* Cards on phones, a table from md up */}
                                <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800 rounded-xl border border-gray-200 dark:border-gray-800">
                                  {filteredProgress.map((p) => (
                                    <div key={`${p.student_id}-${p.lesson_id}`} className="p-3">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{p.student_name}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.student_email}</p>
                                        </div>
                                        <StatusBadge status={p.status} size="sm" />
                                      </div>
                                      <p className="mt-1.5 text-sm text-gray-700 dark:text-gray-300">{p.lesson_title}</p>
                                    </div>
                                  ))}
                                </div>
                                <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
                                  <table className="table">
                                    <thead>
                                      <tr>
                                        <th>Student</th>
                                        <th>Email</th>
                                        <th>Lesson</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredProgress.map((p) => (
                                        <tr key={`${p.student_id}-${p.lesson_id}`}>
                                          <td className="font-medium !text-gray-900 dark:!text-gray-100">{p.student_name}</td>
                                          <td className="!text-gray-500 dark:!text-gray-400">{p.student_email}</td>
                                          <td>{p.lesson_title}</td>
                                          <td><StatusBadge status={p.status} size="sm" /></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </>
        ) : (
          <ContentLibrary />
        )}

        {/* Submissions Panel */}
        {viewingSubmissions && selectedClass && (
          <SubmissionsPanel
            classId={selectedClass.id}
            className={selectedClass.name}
            lessons={lessonsForReview}
            onClose={() => setViewingSubmissions(false)}
          />
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard;
